import { CommonModule } from '@angular/common'
import {
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  OnInit,
  SecurityContext,
  signal,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { DomSanitizer } from '@angular/platform-browser'
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router'
import { OcBreadcrumbItem, OcDropdownImports } from 'otimus-library'
import { filter, map, startWith, Subject, switchMap, takeUntil, timer } from 'rxjs'
import { AuthService } from 'src/app/services/auth.service'
import { BreadcrumbService } from 'src/app/services/breadcrumb.service'
import {
  InternationalizationService,
  type LanguageCode,
} from 'src/app/services/internationalization.service'
import { SidebarService } from 'src/app/services/sidebar.service'
import { SystemInfoService } from 'src/app/services/system-info.service'
import { SidebarFavoritesComponent } from './components/sidebar-favorites/sidebar-favorites.component'
import { SidebarNavGroupComponent } from './components/sidebar-nav-group/sidebar-nav-group.component'
import { FlatNavigationItem } from './entities/flat-navigation-item.entity'
import { NavigationMenuItem, NavigationMenuProgram } from './entities/navigation-menu-item.entity'

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ...OcDropdownImports,
    SidebarNavGroupComponent,
    SidebarFavoritesComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef)
  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly navItemMouseEnter$ = new Subject<void>()
  private readonly navMouseLeave$ = new Subject<void>()

  protected readonly languages: readonly { code: LanguageCode; label: string }[] = [
    { code: 'ptbr', label: 'pt-br' },
    { code: 'enus', label: 'en-us' },
    { code: 'esch', label: 'es-ch' },
  ]
  protected readonly currentLang = signal<LanguageCode>('ptbr')
  protected readonly currentLangLabel = computed(
    () => this.languages.find((l) => l.code === this.currentLang())?.label ?? 'pt-br'
  )

  protected readonly currentTheme = signal<'light' | 'dark'>('light')
  protected readonly navigationItems = signal<NavigationMenuItem[] | null>(null)
  protected readonly searchTerm = signal('')
  protected readonly profilePicUrl = signal<string | null>(null)
  protected readonly personName = signal('')
  protected readonly companyName = signal('')
  protected readonly systemVersion = signal('')
  protected readonly helpMessageHtml = signal<string>('')
  protected readonly loadingMenuItens = signal(false)

  private readonly flatItems = computed<FlatNavigationItem[]>(() => {
    const items = this.navigationItems()
    if (!items) {
      return []
    }
    return this.flattenNavigationItems(items)
  })

  protected readonly filteredItems = computed<FlatNavigationItem[]>(() => {
    const term = this.searchTerm().trim()
    if (!term) {
      return []
    }
    return this.findMatchingItems(term)
  })

  protected readonly isSearching = computed(() => this.searchTerm().trim().length > 0)

  protected readonly favoriteIds = computed(
    () => new Set(this.sidebarService.favoritePrograms().map((f) => f.id))
  )

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  )

  protected readonly activeModuleIds = computed<Set<number>>(() => {
    const url = this.currentUrl()
    const items = this.navigationItems()
    const active = new Set<number>()
    if (!items) {
      return active
    }
    for (const item of items) {
      if (this.moduleContainsUrl(item, url)) {
        active.add(item.id)
      }
    }
    return active
  })

  private readonly activePathIds = computed<Set<number>>(() => {
    const url = this.currentUrl()
    const items = this.navigationItems()
    const path = new Set<number>()
    if (!items) {
      return path
    }
    for (const item of items) {
      this.collectActivePathForModule(item, url, path)
    }
    return path
  })

  protected isModuleSelected(item: NavigationMenuItem): boolean {
    return this.activeModuleIds().has(item.id)
  }

  constructor(
    private readonly authService: AuthService,
    private readonly sidebarService: SidebarService,
    private readonly systemInfoService: SystemInfoService,
    protected readonly translateService: InternationalizationService,
    private readonly sanitizer: DomSanitizer,
    private readonly breadcrumbService: BreadcrumbService
  ) {
    effect(() => {
      const expanded = !this.sidebarService.collapsed()
      const path = this.activePathIds()
      if (expanded && path.size > 0) {
        this.sidebarService.expandIds(path)
      }
    })

    effect(() => {
      const url = this.currentUrl()
      const items = this.navigationItems()
      // Track language changes so crumb labels re-translate when the user
      // switches language (langVersion bumps inside getTranslation calls below).
      this.translateService.language()
      if (!items) {
        this.breadcrumbService.clear()
        return
      }
      const crumbs = this.buildBreadcrumbForUrl(items, url)
      this.breadcrumbService.setPath(crumbs)
    })

    effect(() => {
      this.sidebarService.flatMenuItems.set(this.flatItems())
    })
  }

  private buildBreadcrumbForUrl(items: NavigationMenuItem[], url: string): OcBreadcrumbItem[] {
    for (const module of items) {
      const programPath: NavigationMenuProgram[] = []
      for (const child of module.children) {
        if (this.findLeafPath(child, url, programPath)) {
          return this.materializeCrumbs(module, programPath)
        }
      }
    }
    return []
  }

  private findLeafPath(
    program: NavigationMenuProgram,
    url: string,
    acc: NavigationMenuProgram[]
  ): boolean {
    const programUrl = program.angularPath
      ? `/app/${program.angularPath}`
      : program.systemName
        ? `/gx/${program.systemName}`
        : null

    acc.push(program)

    if (programUrl && this.urlMatches(programUrl, url)) {
      return true
    }
    if (program.children) {
      for (const child of program.children) {
        if (this.findLeafPath(child, url, acc)) {
          return true
        }
      }
    }
    acc.pop()
    return false
  }

  private materializeCrumbs(
    module: NavigationMenuItem,
    programs: NavigationMenuProgram[]
  ): OcBreadcrumbItem[] {
    const crumbs: OcBreadcrumbItem[] = [
      {
        label: this.translateService.getTranslation(module.nameCode ?? module.name),
        icon: module.icon || undefined,
      },
    ]
    programs.forEach((p, index) => {
      const isLeaf = index === programs.length - 1
      const route = isLeaf
        ? p.angularPath
          ? `/app/${p.angularPath}`
          : p.systemName
            ? `/gx/${p.systemName}`
            : undefined
        : undefined
      crumbs.push({
        label: this.translateService.getTranslation(
          p.displayNameCode ?? p.displayName ?? p.systemName ?? ''
        ),
        route,
      })
    })
    return crumbs
  }

  ngOnInit(): void {
    this.loadMenuItems()
    this.loadUserProfile()
    this.loadSystemVersion()
    this.loadHelpMessage()
    this.setupHoverStreams()
    this.setupCloseMobileOnNavigation()
    this.syncLanguageWithUrl()
  }

  private syncLanguageWithUrl(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const urlLang = params.get('lang') as LanguageCode | null
      const storedLang = this.translateService.getCurrentLanguage()
      const next: LanguageCode = urlLang && this.isValidLang(urlLang) ? urlLang : storedLang
      if (next !== storedLang) {
        this.translateService.setLanguage(next)
      }
      if (next !== this.currentLang()) {
        this.currentLang.set(next)
      }
    })
  }

  private isValidLang(code: string): code is LanguageCode {
    return this.languages.some((l) => l.code === code)
  }

  protected changeTheme(theme: 'light' | 'dark'): void {
    if (theme === 'dark') {
      return
    }
    if (theme === this.currentTheme()) {
      return
    }
    this.currentTheme.set(theme)
  }

  protected changeLanguage(code: LanguageCode): void {
    if (code === this.currentLang()) {
      return
    }
    this.currentLang.set(code)
    this.translateService.changeLanguage(code).subscribe()
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { lang: code },
      queryParamsHandling: 'merge',
    })
  }

  @HostListener('document:keydown.escape')
  protected onEscapeKey(): void {
    if (this.isMobileOpen) {
      this.closeMobile()
    }
  }

  private setupCloseMobileOnNavigation(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.sidebarService.closeMobile()
      })
  }

  private setupHoverStreams(): void {
    this.navItemMouseEnter$
      .pipe(
        switchMap(() => timer(300).pipe(takeUntil(this.navMouseLeave$))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.isCollapsed) {
          this.sidebarService.expandByHover()
        }
      })

    this.navMouseLeave$
      .pipe(
        switchMap(() => timer(300).pipe(takeUntil(this.navItemMouseEnter$))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.sidebarService.collapseByHover()
      })
  }

  protected get isCollapsed(): boolean {
    return this.sidebarService.collapsed()
  }

  protected get isPinned(): boolean {
    return this.sidebarService.manuallyOpened()
  }

  protected get isMobileOpen(): boolean {
    return this.sidebarService.mobileOpen()
  }

  protected openMobile(): void {
    return this.sidebarService.openMobile()
  }

  protected closeMobile(): void {
    return this.sidebarService.closeMobile()
  }

  protected toggle(): void {
    return this.sidebarService.toggle()
  }

  protected logout(): void {
    this.authService.logout()
  }

  protected onNavItemMouseEnter(): void {
    this.navItemMouseEnter$.next()
  }

  protected onNavMouseLeave(): void {
    this.navMouseLeave$.next()
  }

  protected isItemExpanded(id: number): boolean {
    return this.sidebarService.isExpanded(id)
  }

  protected toggleItem(id: number): void {
    const siblings = this.navigationItems()?.map((item) => item.id) ?? []
    this.sidebarService.toggleExpanded(id, siblings)
  }

  protected onModuleButtonClick(item: NavigationMenuItem): void {
    if (this.isCollapsed) {
      this.sidebarService.expandByHover()
      if (!this.isItemExpanded(item.id)) {
        this.toggleItem(item.id)
      }
      return
    }
    this.toggleItem(item.id)
  }

  protected childIds(children: NavigationMenuProgram[] | undefined): number[] {
    return children?.map((child) => child.id) ?? []
  }

  protected onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value
    this.searchTerm.set(value)
  }

  private collectActivePathForModule(
    item: NavigationMenuItem,
    url: string,
    out: Set<number>
  ): boolean {
    let matches = this.urlMatches(`/${item.name}`, url)
    for (const child of item.children) {
      if (this.collectActivePathForProgram(child, url, out)) {
        matches = true
      }
    }
    if (matches) {
      out.add(item.id)
    }
    return matches
  }

  private collectActivePathForProgram(
    program: NavigationMenuProgram,
    url: string,
    out: Set<number>
  ): boolean {
    const programUrl = program.angularPath
      ? `/app/${program.angularPath}`
      : program.systemName
        ? `/gx/${program.systemName}`
        : null
    let matches = !!programUrl && this.urlMatches(programUrl, url)
    if (program.children) {
      for (const child of program.children) {
        if (this.collectActivePathForProgram(child, url, out)) {
          matches = true
        }
      }
    }
    if (matches) {
      out.add(program.id)
    }
    return matches
  }

  private moduleContainsUrl(item: NavigationMenuItem, url: string): boolean {
    if (this.urlMatches(`/${item.name}`, url)) {
      return true
    }
    for (const child of item.children) {
      if (this.programContainsUrl(child, url)) {
        return true
      }
    }
    return false
  }

  private programContainsUrl(program: NavigationMenuProgram, url: string): boolean {
    const programUrl = program.angularPath
      ? `/app/${program.angularPath}`
      : program.systemName
        ? `/gx/${program.systemName}`
        : null
    if (programUrl && this.urlMatches(programUrl, url)) {
      return true
    }
    if (program.children) {
      for (const child of program.children) {
        if (this.programContainsUrl(child, url)) {
          return true
        }
      }
    }
    return false
  }

  private urlMatches(itemUrl: string, currentUrl: string): boolean {
    let base = currentUrl.split('?')[0].split('#')[0]
    try {
      base = decodeURIComponent(base)
    } catch {
      /* keep raw base if decode fails */
    }
    base = base.toLowerCase()
    const needle = (itemUrl.startsWith('/') ? itemUrl : '/' + itemUrl).toLowerCase()
    let fromIndex = 0
    while (fromIndex <= base.length) {
      const idx = base.indexOf(needle, fromIndex)
      if (idx === -1) {
        return false
      }
      const next = base[idx + needle.length]
      if (next === undefined || next === '/' || next === '?' || next === '#') {
        return true
      }
      fromIndex = idx + 1
    }
    return false
  }

  private fuzzyMatch(search: string, target: string): boolean {
    const searchLower = search.toLowerCase()
    const targetLower = target.toLowerCase()
    let searchIndex = 0
    for (let i = 0; i < targetLower.length && searchIndex < searchLower.length; i++) {
      if (targetLower[i] === searchLower[searchIndex]) {
        searchIndex++
      }
    }
    return searchIndex === searchLower.length
  }

  private findMatchingItems(term: string): FlatNavigationItem[] {
    return this.flatItems().filter((item) => this.fuzzyMatch(term, item.label))
  }

  private flattenNavigationItems(items: NavigationMenuItem[]): FlatNavigationItem[] {
    const result: FlatNavigationItem[] = []
    for (const item of items) {
      if (item.children.length === 0) {
        result.push({
          id: item.id,
          label: item.name,
          route: item.name,
          icon: item.icon,
          parentName: item.name,
        })
      } else {
        this.flattenPrograms(item.children, item.name, item.icon, result)
      }
    }
    return result
  }

  private flattenPrograms(
    programs: NavigationMenuProgram[],
    parentName: string,
    parentIcon: string | null,
    result: FlatNavigationItem[]
  ): void {
    for (const program of programs) {
      if (program.children && program.children.length > 0) {
        this.flattenPrograms(program.children, parentName, parentIcon, result)
      } else {
        result.push({
          id: program.id,
          label: this.translateService.getTranslation(
            program.displayName ?? program.systemName ?? ''
          ),
          route: program.angularPath
            ? '/app/' + program.angularPath
            : '/gx/' + (program.systemName ?? ''),
          icon: parentIcon,
          parentName,
        })
      }
    }
  }

  private loadHelpMessage(): void {
    this.systemInfoService
      .findSystemHelpMessage()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        this.helpMessageHtml.set(
          this.sanitizer.sanitize(SecurityContext.HTML, response.data.systemMessage) ?? ''
        )
      })
  }

  private loadSystemVersion(): void {
    this.systemInfoService
      .findSystemVersion()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        this.systemVersion.set(response.data.version)
      })
  }

  private loadUserProfile(): void {
    const user = this.authService.user
    if (!user) {
      return
    }
    this.personName.set(user.personName ?? '')
    this.profilePicUrl.set(user.profilePicUrl ?? null)
    this.companyName.set(user.companyName ?? '')
  }

  protected onFavoriteDeleted(programId: number): void {
    this.sidebarService.favoritePrograms.update((list) =>
      list.filter((fav) => fav.id !== programId)
    )
  }

  private loadMenuItems(): void {
    this.loadingMenuItens.set(true)
    this.sidebarService.collapsed.set(true)
    this.sidebarService
      .findMenuItems()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          if (data.data.modules.length <= 0) {
            this.navigationItems.set([])
            return
          }
          function sortMenuItems(items?: NavigationMenuItem[]): NavigationMenuItem[] | null {
            if (!items) {
              return null
            }
            return items
              .map((item) => {
                return {
                  ...item,
                  children: sortMenuItems(
                    item.children as unknown as NavigationMenuItem[]
                  ) as unknown as NavigationMenuProgram[],
                }
              })
              .sort((item1, item2) => {
                return item1.order - item2.order
              })
          }
          const sorted = sortMenuItems(data.data.modules)
          this.navigationItems.set(sorted)
          this.sidebarService.favoritePrograms.set(data.data.favoritePrograms)
        },
        complete: () => {
          this.loadingMenuItens.set(false)
        },
      })
  }
}
